import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { RoutesLink } from "@/utility/RouteLinks";
import { PencilIcon, Package, Tag, Layers, Info, CheckCircle2, AlertCircle, Plus, ChefHat, Phone, MapPin, PackagePlus, PercentSquare, } from "lucide-react";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { IProduct, ISingleProductResponse } from "@/interfaces/Product/product.interface";
import { formatRand } from "@/utility/helper";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SectionCard } from "@/components/common/SectionCard";
import { DetailItem } from "@/components/common/DetailItem";
import { DetailsGallery } from "@/components/common/DetailsGallery";
import { StarRating } from "@/components/common/StarRating";
import { ReviewCard } from "@/components/common/ReviewCard";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";


export const ProductView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useRoleNavigate();
    const backNavigate = useNavigate();
    const [product, setProduct] = useState<IProduct | null>(null);
    const [loading, setLoading] = useState(true);
    const { canEdit, isAdmin } = usePermissions();

    const fetchProductDetails = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await callApi<ISingleProductResponse>(
                `${API_URL + API_ENDPOINT.products}/${id}`,
                { method: 'GET' }
            );
            setProduct(response?.data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductDetails();
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout>
                <div className="text-center py-20">
                    <h2 className="text-xl font-bold">Product not found</h2>
                    <button onClick={() => navigate(RoutesLink.Products)} className="mt-4 text-primary hover:underline">
                        Back to products
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <MainHeading
                title={product.name}
                description="View and analyze detailed product information"
                showBack
                onBack={() => backNavigate(-1)}
                actionLabel={canEdit(RESOURCES.PRODUCT) ? "Edit Product" : ""}
                actionIcon={<PencilIcon className="w-4 h-4" />}
                onAction={() => navigate(RoutesLink.EditProduct.replace(':id', product.id))}
            />

            <div className="mt-8 grid items-start grid-cols-1 xl:grid-cols-3 gap-8 pb-20">
                {/* ── VISUALS & CORE ACTION ── */}
                <div className="xl:col-span-1 space-y-6">
                    <DetailsGallery
                        media={product.media || []}
                        name={product.name}
                        status={product.status}
                        className=""
                    />
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-xl bg-primary/5 border border-primary/10">
                            <div className="flex flex-col">
                                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Current Price</span>
                                <span className="text-2xl font-black text-primary">{formatRand(product.price)}</span>
                                <div className="mt-1 flex items-center gap-2">
                                    <StarRating
                                        rating={Number(product.avgRating) || 0}
                                        starSize="w-3.5 h-3.5"
                                    />
                                    <span className="text-[10px] text-muted-foreground font-bold">
                                        ({product.ratingCount || 0} Reviews)
                                    </span>
                                </div>
                            </div>
                            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                                <Tag className="w-5 h-5" />
                            </div>
                        </div>

                        {/* Tags Section */}
                        {product.productTags && product.productTags.length > 0 && (
                            <DetailItem
                                label="Product Tags"
                                icon={<Tag />}
                                bordered
                                className="mb-4 bg-primary/5 border-primary/10"
                                iconClassName="bg-primary/20 text-primary"
                                value={
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {product.productTags.map((item: any) => (
                                            <div
                                                key={item.tagId}
                                                className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-primary/10 rounded-lg shadow-sm"
                                            >
                                                <Tag className="w-2.5 h-2.5 text-primary" />
                                                <span className="text-[10px] font-bold text-foreground">
                                                    {item.tag?.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                }
                            />
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <DetailItem
                                label="Stock"
                                value={`${product.productInfo?.stockQuantity || 0} Units`}
                                icon={<Package />}
                                bordered
                                iconClassName="bg-orange-100 text-orange-600"
                            />
                            <DetailItem
                                label="VAT Included"
                                value={<StatusBadge status={product.vatStatus === 'active' ? 'Yes' : 'No'} />}
                                icon={<PercentSquare />}
                                bordered
                                iconClassName="bg-purple-100 text-purple-600"
                            />
                            <DetailItem
                                label="Category"
                                bordered
                                value={
                                    <div className="flex flex-col">
                                        <span className="truncate max-w-[120px]">{product.category?.name || '---'}</span>
                                        {product.subCategory && (
                                            <span className="text-[10px] text-primary font-semibold truncate max-w-[120px]">
                                                {product.subCategory.name}
                                            </span>
                                        )}
                                    </div>
                                }
                                icon={<Layers />}
                                iconClassName="bg-blue-100 text-blue-600"
                            />
                        </div>
                    </div>
                    <SectionCard
                        title="Product Variants"
                        icon={<PackagePlus />}
                        className="border-none shadow-none p-0 "
                        extra={
                            <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                                {product.variants?.length || 0} Total
                            </span>
                        }
                    >
                        {product.variants && product.variants.length > 0 ? (
                            <div className="space-y-3">
                                {product.variants.map((variant) => (
                                    <div key={variant.id} className="p-3 rounded-xl border border-secondary/10 bg-secondary/5 hover:bg-secondary/10 transition-colors">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-bold text-foreground truncate max-w-[120px]">{variant.name}</span>
                                            <StatusBadge status={variant.status} />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-[9px] text-muted-foreground uppercase font-bold">Price</span>
                                                <span className="text-sm font-black text-primary">{formatRand(variant.price)}</span>
                                            </div>
                                            <div className="flex flex-col items-end text-right">
                                                <span className="text-[9px] text-muted-foreground uppercase font-bold">Unit</span>
                                                <span className="text-xs font-semibold text-foreground italic">{variant.unit || '---'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted-foreground italic text-xs">
                                No variants configured.
                            </div>
                        )}
                    </SectionCard>
                </div>

                <div className="xl:col-span-2 space-y-6">
                    <SectionCard title="Product description" icon={<Info />}>
                        <p className="text-muted-foreground leading-relaxed text-sm whitespace-pre-wrap">
                            {product.productInfo?.descriptionLong || "No long description provided for this product."}
                        </p>

                        {product.productInfo?.additionalInfo && (
                            <div className="mt-8 pt-6 border-t border-dashed">
                                <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Specifications</h4>
                                <div className="bg-secondary/5 rounded-xl p-4 text-sm text-foreground border border-secondary/10">
                                    {product.productInfo.additionalInfo.replace(/^["']|["']$/g, '')}
                                </div>
                            </div>
                        )}

                        <div className="mt-8 pt-6 border-t border-dashed">
                            <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Product Source</h4>
                            <div className={`text-sm font-semibold px-4 py-2 rounded-xl w-fit ${product.isGlobal
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                                }`}>
                                {product.isGlobal ? "Global Product (Admin Created)" : "Store Product (Manually Created)"}
                            </div>
                        </div>
                    </SectionCard>

                    {/* Addons Section */}
                    <SectionCard
                        title="Product Addons"
                        icon={<Plus />}
                        extra={
                            <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                                {product.addons?.length || 0} Total
                            </span>
                        }
                    >
                        {product.addons && product.addons.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {product.addons.map((addon) => (
                                    <DetailItem
                                        key={addon.id}
                                        label={addon.name}
                                        variant="horizontal"
                                        value={
                                            <div className="flex flex-col items-start gap-0.5">
                                                <span>{formatRand(addon.price)}</span>
                                                <span className="text-[10px] text-muted-foreground font-bold tracking-wide">
                                                    STOCK: {addon.inventory ?? 0}
                                                </span>
                                            </div>
                                        }
                                        icon={addon.status === 'active' ? <CheckCircle2 /> : <AlertCircle />}
                                        iconClassName={addon.status === 'active' ? 'text-green-500' : 'text-destructive'}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-muted-foreground italic text-sm">
                                This product has no extra addons configured.
                            </div>
                        )}
                    </SectionCard>

                    {/* Store Info Section */}
                    {product.store && isAdmin && (
                        <SectionCard
                            title="Store Information"
                            icon={<ChefHat />}
                            extra={
                                <StatusBadge status={product.store.status} />
                            }
                        >
                            <div className="flex flex-col gap-4">
                                <div>
                                    <h4 className="text-sm font-bold text-foreground mb-1">{product.store.name}</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        {product.store.description || "No description provided for this store."}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                    <DetailItem
                                        label="Contact"
                                        value={product.store.contact || "N/A"}
                                        icon={<Phone />}
                                        bordered
                                        className="bg-secondary/10 border-secondary/20"
                                    />
                                    <DetailItem
                                        label="Address"
                                        value={product.store.address || "N/A"}
                                        icon={<MapPin />}
                                        bordered
                                        className="bg-secondary/10 border-secondary/20"
                                    />
                                </div>
                            </div>
                        </SectionCard>
                    )}

                    {/* Customer Reviews Section */}
                    {id && (
                        <ReviewCard
                            targetType="on_product"
                            targetId={id}
                            title="Product Reviews & Ratings"
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
};
